import { FishingReport, Product, SeasonGroup, Species, WaterbodyDetail } from '@omniafishing/core';
import { Affix, Breadcrumb } from 'antd';
import classNames from 'classnames';
import _ from 'lodash';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { getEnv } from '../../../../env';
import { useUser } from '../../../../hooks/use_user';
import { DateOrTimeInput } from '../../../../lib/time';
import { OmniaUrls } from '../../../../lib/urls';
import { ContentWrapper } from '../../../content_wrapper/content_wrapper';
import { PublishedDates } from '../../../published_dates/published_dates';
import { SeasonSpeciesSelects } from '../../../season_species_selects/season_species_selects';
import { FIRST_PUBLISHED_DATE } from '../../waterbody_detail';
import styles from './waterbody_detail_header.less';
import { WaterbodyDetailHeaderButtons } from './waterbody_detail_header_buttons';

interface WaterbodyDetailHeaderProps {
  fishingReports?: FishingReport[];
  headingTag: 'p' | 'h1';
  lastUpdatedDate?: DateOrTimeInput;
  navElement: React.ReactNode;
  productToShowInFollowModal: Product;
  scrollToFishingReport: () => void;
  showPublishDates?: boolean;
  showButtons?: boolean;
  showBreadcrumbs?: boolean;
  selectedSeasonGroup?: SeasonGroup;
  selectedSpecies?: Species;
  waterbody: WaterbodyDetail;
}

export const WaterbodyDetailHeader = (props: WaterbodyDetailHeaderProps) => {
  const {
    fishingReports,
    headingTag: HeadingTag,
    lastUpdatedDate,
    navElement,
    productToShowInFollowModal,
    scrollToFishingReport,
    showPublishDates,
    showBreadcrumbs = true,
    showButtons = true,
    selectedSeasonGroup,
    selectedSpecies,
    waterbody,
  } = props;

  const { page_heading, primary_name, season_groups, species } = waterbody;
  const waterbodySpeciesSorted = _.sortBy(species, ['recommendations_order']);
  const currentSeasonGroup = season_groups.find((sg) => sg.active);
  const { user } = useUser();

  const [isSticky, setIsSticky] = useState(false);

  return (
    <>
      <div className={styles.headerContainer}>
        <Affix onChange={(affixed) => setIsSticky(affixed)}>
          <div className={styles.headerBackground}>
            <ContentWrapper>
              <div
                className={classNames(styles.lakeDetailsHeader, {
                  [styles.lakeDetailsHeader__sticky]: isSticky,
                })}
              >
                {showBreadcrumbs && (
                  <Breadcrumb
                    className={classNames(styles.breadcrumb, {
                      [styles.breadcrumb__sticky]: isSticky,
                    })}
                  >
                    <Breadcrumb.Item>
                      {waterbody.locales.map((locale, i) => {
                        return [
                          i > 0 && ', ',
                          <Link to={OmniaUrls.state(locale.state)} key={locale.state.abbr}>
                            {locale.state.abbr.toUpperCase()}
                          </Link>,
                        ];
                      })}
                    </Breadcrumb.Item>
                    <Breadcrumb.Item>{primary_name}</Breadcrumb.Item>
                  </Breadcrumb>
                )}
                <div className={styles.nameAndSelects}>
                  <div>
                    <HeadingTag
                      className={classNames(styles.lakeName, {
                        [styles.lakeName__sticky]: isSticky,
                      })}
                    >
                      {page_heading || <span>{primary_name}</span>}
                      {user?.admin && (
                        <a
                          href={`${getEnv().API_BASE_URL}admin/waterbodies/${
                            waterbody.waterbody_id
                          }/edit`}
                          className={styles.adminLink}
                        >
                          Edit
                        </a>
                      )}
                    </HeadingTag>
                    {showPublishDates && (
                      <PublishedDates
                        lastUpdatedDate={lastUpdatedDate}
                        firstPublishedDate={FIRST_PUBLISHED_DATE}
                        className={styles.publishedDatesWrapper}
                      />
                    )}
                    {selectedSpecies && selectedSeasonGroup && (
                      <div className={styles.selectsContainer}>
                        <SeasonSpeciesSelects
                          selectedSeasonGroup={selectedSeasonGroup}
                          selectedSpecies={selectedSpecies}
                          seasonGroups={season_groups}
                          speciesOptions={waterbodySpeciesSorted}
                          currentSeasonGroup={currentSeasonGroup}
                          fishingReports={fishingReports}
                        />
                      </div>
                    )}
                  </div>
                  {showButtons && (
                    <div>
                      <WaterbodyDetailHeaderButtons
                        waterbody={waterbody}
                        scrollToFishingReport={scrollToFishingReport}
                        isSticky={isSticky}
                        productToShowInFollowModal={productToShowInFollowModal}
                      />
                    </div>
                  )}
                </div>
              </div>
            </ContentWrapper>
            {navElement}
          </div>
        </Affix>
      </div>
    </>
  );
};
